import { message } from 'antd';
import { useCallback, useContext } from 'react';
import { useIntl } from 'react-intl';
import { useHistory, useLocation } from 'react-router-dom';
import FileVersionsContext from '~src/contexts/FileVersionsContext';
import LargeFilesContext from '~src/contexts/LargeFilesContext';
import { useIsPath } from '~src/hooks/useIsPath';
import { useTenderParamsFromUrl } from '~src/hooks/useTenderParamsFromUrl';
import {
  DbAttachment,
  DbFile,
  DbFolder,
  DbModuleFile,
  DbTenderFolder,
} from '~src/types/db';
import {
  ClassicRegistration,
  downloadClassicRegistrationReport,
} from '~src/services/api/modules';
import { saveAs } from 'file-saver';
import {
  downloadFileLastVersion,
  downloadFolder,
  downloadModuleFile,
  downloadMultiple,
  downloadMultipleModuleFiles,
  downloadMultipleVersionFiles,
  downloadMultipleVersionFolderFiles,
  downloadTenderFolder,
  downloadVersionedFile,
  downloadVersionedFolderFile,
  downloadTenderMultiple,
  downloadVendorTenderFolderFile,
  downloadVendorTenderFolderMultiple,
  downloadTenderFolderMultipleItems,
  downloadMultipleClassicRegistrations,
} from '~src/utils/download';
import { appLog } from '~src/utils/log';
import { useStore } from '~src/store';
import { loadFileLog } from '~src/services/api/actionLogs';
import { downloadTenderDocumentMultiple } from '~src/utils/downloader/downloadTenderDocumentMultiple';
import { downloadTenderShareFolder } from '~src/utils/downloader/tenderShareFolderDownload';
import {
  loadSharedTenderAttachment,
  loadSharedTenderVersionedFile,
} from '~src/services/api/shares';
import { saveFromLink } from '~src/utils/downloader';
import { downloadShareTenderMultiple } from '~src/utils/downloader/downloadShareTenderMultiple';

type InputType = {
  files: (DbFile & DbAttachment)[];
  folders: DbFolder[];
  moduleFiles: DbModuleFile[];
  tenderFolders: DbTenderFolder[];
  classicRegistrations?: ClassicRegistration[];
};

type DownloadHandlerOptions = {
  onResetItemsList: () => void;
  allFolders?: DbFolder[];
};

export const useCherryPickFileDownload = () => {
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();

  const { fileVersionsContext } = useContext(FileVersionsContext);
  const { setLargeFilesContext } = useContext(LargeFilesContext);

  const isOnArchive = useIsPath('/archive');
  const isOnTender = useIsPath('/tenders');
  const isOnTenderFolder = useIsPath('/tender-folders');
  const isOnMyTenders = useIsPath('/my-tenders');
  const isOnRecents = useIsPath('/recents');
  const isOnFavourites = useIsPath('/favourites');
  const isOnSearch = useIsPath('/search');
  const isOnModule = useIsPath('/module');
  const isOnRegistrations = useIsPath('/section-modules/sector');

  // JIRA: MVP TV1-362
  const { currentFolderId, rootFolderId, tenderId } = useTenderParamsFromUrl({
    isOnShare: isOnMyTenders,
  });

  const getShareCredentials = useCallback(() => {
    const match = location.pathname.match(/\/my-tenders\/([^\/]+)/);
    const shareIdKey = match ? match[1] : '';
    const parts = shareIdKey.split('-');
    const sId = parts[0];
    const sKey = parts.slice(1).join('-');
    return { shareId: sId, shareKey: sKey };
  }, [location.pathname]);

  const handleMyTendersDownload = useCallback(
    async (
      { files: selectedFiles, tenderFolders: selectedTenderFolders }: InputType,
      { onResetItemsList, allFolders }: DownloadHandlerOptions
    ) => {
      const { shareId, shareKey } = getShareCredentials();

      if (!shareId || !shareKey) {
        message.error('Missing share credentials');
        return onResetItemsList();
      }

      // -------------------------------------------------------------------------
      // CASE 1: Single File Optimization (Direct Download, No ZIP)
      // -------------------------------------------------------------------------
      if (selectedFiles.length === 1 && selectedTenderFolders.length === 0) {
        const file = selectedFiles[0];
        const isFirstLevelDocs = !currentFolderId;

        try {
          if (isFirstLevelDocs) {
            // Root level files (Attachments)
            const res = await loadSharedTenderAttachment({
              file_guid: String(file.id),
              share_id: shareId,
              share_key: shareKey,
            });
            if (res?.location) {
              saveFromLink(res.location);
            }
          } else {
            // VENDOR SUBFOLDER: Get selected version from context or fallback to latest
            const versionId = fileVersionsContext[file.id]?.id;
            const stableVersions = (file.files || [])
              .map((f: any) => ({
                ...f,
                revision: f.serial_number ?? f.version ?? f.revision,
                version: f.serial_number ?? f.version ?? f.revision,
                id: f.registration_instance_id || f.file_id || f.id,
              }))
              .sort((a: any, b: any) => (b.revision || 0) - (a.revision || 0));

            const selectedVersion =
              stableVersions.find(
                (v: any) => String(v.id) === String(versionId)
              ) ||
              stableVersions[0] ||
              file.versions?.find(
                (v: any) => String(v.id) === String(versionId)
              ) ||
              file.versions?.[0];

            if (!selectedVersion) {
              message.error(intl.formatMessage({ id: 'file-download-failed' }));
              return onResetItemsList();
            }

            const registrationId =
              selectedVersion.registration_id ||
              selectedVersion.versioned_file_id ||
              file.id;
            const serialNumber =
              selectedVersion.version || selectedVersion.serial_number;

            // VENDOR SUBFOLDER
            await downloadVendorTenderFolderFile({
              shareId,
              shareKey,
              rootFolderId: String(rootFolderId),
              folderId: String(currentFolderId),
              registrationId: String(registrationId),
              serialNumber,
              onDownloadStarted: () =>
                message.info(intl.formatMessage({ id: 'download-starting' })),
            });
          }
        } catch (err) {
          appLog('handleMyTendersDownload -> single file error', err);
          message.error(intl.formatMessage({ id: 'file-download-failed' }));
        }
        return onResetItemsList();
      }

      // -------------------------------------------------------------------------
      // CASE 2: Multiple Files or Folders (Batch ZIP Download)
      // -------------------------------------------------------------------------
      const isFirstLevelDocs = !currentFolderId;

      if (isFirstLevelDocs) {
        // Root level docs fallback to old method
        await downloadShareTenderMultiple({
          folders: selectedTenderFolders,
          files: selectedFiles,
          allFolders: allFolders || [],
          shareId,
          shareKey,
          currentFolderId,
          rootFolderId,
          onDownloadStarted: () => {
            message.info(intl.formatMessage({ id: 'download-starting' }));
          },
        }).catch((err) => {
          appLog('handleMyTendersDownload -> batch error', err);
          message.error(intl.formatMessage({ id: 'file-download-failed' }));
        });
      } else {
        // Vendor Subfolders: use new multiple downloader with versions
        await downloadVendorTenderFolderMultiple({
          folders: selectedTenderFolders,
          files: selectedFiles,
          allFolders: allFolders || [],
          shareId,
          shareKey,
          currentFolderId,
          rootFolderId,
          fileVersionsContext,
          onDownloadStarted: () => {
            message.info(intl.formatMessage({ id: 'download-starting' }));
          },
        }).catch((err) => {
          appLog('handleMyTendersDownload -> batch error', err);
          message.error(intl.formatMessage({ id: 'file-download-failed' }));
        });
      }

      return onResetItemsList();
    },
    [
      currentFolderId,
      rootFolderId,
      getShareCredentials,
      intl,
      fileVersionsContext,
    ]
  );
  // JIRA: MVP TV1-362 - end

  const handleArchiveFilesDownload = useCallback(
    async (
      {
        files: selectedFiles,
        folders: selectedFolders,
        moduleFiles: selectedModuleFiles,
        tenderFolders: selectedTenderFolders,
      }: InputType,
      { onResetItemsList, allFolders }: DownloadHandlerOptions
    ) => {
      if (
        !selectedFiles.length &&
        !selectedFolders.length &&
        !selectedModuleFiles &&
        !selectedTenderFolders.length
      ) {
        return onResetItemsList();
      }

      // single file download
      if (
        selectedFiles.length === 1 &&
        !selectedFolders.length &&
        !selectedModuleFiles.length &&
        !selectedTenderFolders.length
      ) {
        await downloadFileLastVersion(
          selectedFiles[0],
          fileVersionsContext
        ).catch((err) => {
          appLog('downloadFile -> err', err);
          message.error(intl.formatMessage({ id: 'file-download-failed' }));
        });

        // hide cherry pick panel
        return onResetItemsList();
      }

      // single folder download
      if (
        selectedFolders.length === 1 &&
        !selectedFiles.length &&
        !selectedModuleFiles.length &&
        !selectedTenderFolders.length
      ) {
        useStore.getState().totalDownloadFolders(selectedFolders.length);
        await downloadFolder({
          folder: selectedFolders[0],
          allFolders: allFolders || [],
          fileVersionsContext,
          onDownloadStarted: () => {
            // setTimeout(
            //   () =>
            //     message.info(intl.formatMessage({ id: 'download-starting' })),
            //   400
            // );
          },
          onLargeFiles: (largeFiles) => setLargeFilesContext(largeFiles),
        }).catch((err) => {
          appLog('downloadFolder -> err', err);
          // hidePrepMsg && hidePrepMsg();
          message.error(intl.formatMessage({ id: 'file-download-failed' }));
        });

        // hide cherry pick panel
        return onResetItemsList();
      }

      // multiple items download
      useStore.getState().totalDownloadFolders(selectedFolders.length);
      useStore.getState().totalDownloadFiles(selectedFiles.length);
      // filesDownloaded();
      await downloadMultiple({
        files: selectedFiles,
        folders: selectedFolders,
        moduleFiles: selectedModuleFiles,
        allFolders: allFolders || [],
        fileVersionsContext,
        onDownloadStarted: () => {
          // hidePrepMsg();
          // setTimeout(
          //   () => message.info(intl.formatMessage({ id: 'download-starting' })),
          //   400
          // );
        },
        onLargeFiles: (largeFiles) => setLargeFilesContext(largeFiles),
      }).catch((err) => {
        appLog('downloadMultiple -> err', err);
        // hidePrepMsg && hidePrepMsg();
        message.error(intl.formatMessage({ id: 'file-download-failed' }));
      });

      // hide cherry pick panel
      return onResetItemsList();
    },
    []
  );

  const handleModuleFilesDownload = useCallback(
    async (
      {
        files: selectedFiles,
        folders: selectedFolders,
        moduleFiles: selectedModuleFiles,
        tenderFolders: selectedTenderFolders,
      }: InputType,
      { onResetItemsList, allFolders }: DownloadHandlerOptions
    ) => {
      if (
        !selectedFiles.length &&
        !selectedFolders.length &&
        !selectedModuleFiles &&
        !selectedTenderFolders.length
      ) {
        return onResetItemsList();
      }

      // single module file
      if (
        selectedModuleFiles.length === 1 &&
        !selectedFiles.length &&
        !selectedFolders.length &&
        !selectedTenderFolders.length
      ) {
        //console.log(`downloadModuleFile worked!`, selectedModuleFiles);
        await downloadModuleFile(selectedModuleFiles[0], () => {
          // message.info(intl.formatMessage({ id: 'download-starting' }))
        }).catch((err) => {
          appLog('downloadFile -> err', err);
          message.error(intl.formatMessage({ id: 'file-download-failed' }));
        });

        return onResetItemsList();
      }

      // multiple module files
      if (
        selectedModuleFiles.length > 1 &&
        !selectedFiles.length &&
        !selectedFolders.length &&
        !selectedTenderFolders.length
      ) {
        //console.log(`downloadModuleFiles worked!`, selectedModuleFiles);
        // @ts-ignore
        await downloadMultipleModuleFiles({
          moduleFiles: selectedModuleFiles,
        }).catch((err) => {
          appLog('downloadFile -> err', err);
          message.error(intl.formatMessage({ id: 'file-download-failed' }));
        });

        return onResetItemsList();
      }
    },
    []
  );

  const handleTenderFilesDownload = useCallback(
    async (
      {
        files: selectedFiles,
        folders: selectedFolders,
        moduleFiles: selectedModuleFiles,
        tenderFolders: selectedTenderFolders,
      }: InputType,
      { onResetItemsList, allFolders }: DownloadHandlerOptions
    ) => {
      if (
        !selectedFiles.length &&
        !selectedFolders.length &&
        !selectedModuleFiles &&
        !selectedTenderFolders.length
      ) {
        return onResetItemsList();
      }

      // Check if we are inside a subfolder (Tender Documents)
      const isFolderView = window.location.pathname.includes('tender-folders');

      // Safely extract projectId from URL
      const projectIdMatch = location.pathname.match(/\/projects\/(\d+)/);
      const projectId = projectIdMatch ? Number(projectIdMatch[1]) : undefined;

      // ==========================================
      // 1. SINGLE FILE DOWNLOAD
      // ==========================================
      if (
        selectedFiles.length === 1 &&
        !selectedFolders.length &&
        !selectedModuleFiles.length &&
        !selectedTenderFolders.length
      ) {
        if (isFolderView) {
          const file = selectedFiles[0];

          const selectedRevision = fileVersionsContext[file.id]?.revision;
          let fileDetails = file.files?.find(
            (f: any) => f.serial_number === selectedRevision
          );

          // Fallback to latest serial if specific version not found
          if (!fileDetails && file.files?.length) {
            fileDetails = file.files.reduce((prev: any, current: any) => {
              const prevSerial = prev.serial_number || 0;
              const currSerial = current.serial_number || 0;
              return currSerial > prevSerial ? current : prev;
            });
          }

          if (!fileDetails) {
            console.error('No file found in the folder item');
            return onResetItemsList();
          }

          await downloadVersionedFolderFile(
            Number(fileDetails.project_id),
            Number(tenderId),
            Number(rootFolderId),
            Number(currentFolderId),
            fileDetails.registration_id,
            fileDetails.serial_number,
            () => {
              // message.info('Download started')
            }
          ).catch((err) => {
            appLog('downloadFolderFile -> err', err);
          });

          return onResetItemsList();
        } else {
          // Existing file download logic for root documents view
          const file = selectedFiles[0];
          await downloadVersionedFile(
            Number(tenderId),
            Number(file.project_id),
            String(file.id)
          ).catch((err) => {
            appLog('downloadFile -> err', err);
          });
          return onResetItemsList();
        }
      }

      // ==========================================
      // 2. SINGLE MODULE FILE DOWNLOAD
      // ==========================================
      if (
        selectedModuleFiles.length === 1 &&
        !selectedFiles.length &&
        !selectedFolders.length &&
        !selectedTenderFolders.length
      ) {
        // console.log(`downloadModuleFile cherry picker tender worked!`);
        await downloadModuleFile(selectedModuleFiles[0], () => {
          // message.info(intl.formatMessage({ id: 'download-starting' }))
        }).catch((err) => {
          appLog('downloadFile -> err', err);
          message.error(intl.formatMessage({ id: 'file-download-failed' }));
        });

        return onResetItemsList();
      }

      // ==========================================
      // 3. SINGLE FOLDER DOWNLOAD
      // ==========================================
      if (
        selectedTenderFolders.length === 1 &&
        !selectedFolders.length &&
        !selectedFiles.length &&
        !selectedModuleFiles.length
      ) {
        // ROOT & SUBFOLDER: Use new unified downloader for all tender folders
        await downloadTenderFolderMultipleItems({
          files: [],
          folders: selectedTenderFolders as any,
          allFolders: allFolders || [],
          projectId: Number(projectId),
          tenderId: Number(tenderId),
          rootFolderId: Number(rootFolderId),
          currentFolderId: Number(currentFolderId),
          fileVersionsContext,
          onDownloadStarted: () => {
            // message.info(intl.formatMessage({ id: 'download-starting' }));
          },
        }).catch((err) => {
          appLog('downloadTenderFolderMultipleItems -> err', err);
          message.error(intl.formatMessage({ id: 'file-download-failed' }));
        });

        return onResetItemsList();
      }

      // ==========================================
      // 4. MULTIPLE FOLDERS DOWNLOAD
      // ==========================================
      if (
        selectedTenderFolders.length > 1 &&
        !selectedFolders.length &&
        !selectedFiles.length &&
        !selectedModuleFiles.length
      ) {
        // ROOT & SUBFOLDER: Use new unified downloader for all tender folders
        await downloadTenderFolderMultipleItems({
          files: [],
          folders: selectedTenderFolders as any,
          allFolders: allFolders || [],
          projectId: Number(projectId),
          tenderId: Number(tenderId),
          rootFolderId: Number(rootFolderId),
          currentFolderId: Number(currentFolderId),
          fileVersionsContext,
          onDownloadStarted: () => {
            //
          },
        }).catch((err) => {
          appLog('downloadTenderFolderMultipleItems -> err', err);
          message.error(intl.formatMessage({ id: 'file-download-failed' }));
        });

        return onResetItemsList();
      }

      // ==========================================
      // 5. MULTIPLE FILES DOWNLOAD
      // ==========================================
      if (
        selectedFiles.length > 1 &&
        !selectedFolders.length &&
        !selectedTenderFolders.length &&
        !selectedModuleFiles.length
      ) {
        if (isFolderView) {
          // SUBFOLDER: Use new unified downloader
          await downloadTenderFolderMultipleItems({
            files: selectedFiles,
            folders: [],
            allFolders: allFolders || [],
            projectId: Number(projectId),
            tenderId: Number(tenderId),
            rootFolderId: Number(rootFolderId),
            currentFolderId: Number(currentFolderId),
            fileVersionsContext,
            onDownloadStarted: () => {
              //
            },
          }).catch((err) => {
            appLog('downloadTenderFolderMultipleItems -> err', err);
            message.error(intl.formatMessage({ id: 'file-download-failed' }));
          });
        } else {
          // ROOT FOLDER: Use old legacy logic
          const file = selectedFiles[0];
          await downloadMultipleVersionFiles(
            selectedFiles,
            tenderId,
            file.project_id
          ).catch((err) => {
            appLog('downloadMultiple -> err', err);
            message.error(intl.formatMessage({ id: 'file-download-failed' }));
          });
        }
        return onResetItemsList();
      }

      // ==========================================
      // 6. MIXED DOWNLOAD (FILES + FOLDERS)
      // ==========================================
      if (isFolderView) {
        // SUBFOLDER: Use new unified downloader
        await downloadTenderFolderMultipleItems({
          files: selectedFiles,
          folders: selectedTenderFolders as any,
          allFolders: allFolders || [],
          projectId: Number(projectId),
          tenderId: Number(tenderId),
          rootFolderId: Number(rootFolderId),
          currentFolderId: Number(currentFolderId),
          fileVersionsContext,
          onDownloadStarted: () => {
            //
          },
        }).catch((err) => {
          appLog('downloadTenderFolderMultipleItems -> err', err);
          message.error(intl.formatMessage({ id: 'file-download-failed' }));
        });
        return onResetItemsList();
      } else {
        // ROOT FOLDER: Use old legacy logic
        await downloadTenderDocumentMultiple({
          files: selectedFiles,
          folders: selectedTenderFolders as any,
          moduleFiles: selectedModuleFiles,
          allFolders: allFolders || [],
          tenderId,
          fileVersionsContext,
          onDownloadStarted: () => {
            //
          },
          onLargeFiles: (largeFiles) => setLargeFilesContext(largeFiles),
        }).catch((err) => {
          appLog('downloadMultiple -> err', err);
          message.error(intl.formatMessage({ id: 'file-download-failed' }));
        });
        return onResetItemsList();
      }
    },
    []
  );

  // JUST tender folder w-out root folder
  const handleTenderFoldersFilesDownload = useCallback(
    async (
      {
        files: selectedFiles,
        folders: selectedFolders,
        moduleFiles: selectedModuleFiles,
        tenderFolders: selectedTenderFolders,
      }: InputType,
      { onResetItemsList, allFolders }: DownloadHandlerOptions
    ) => {
      if (
        !selectedFiles.length &&
        !selectedFolders.length &&
        !selectedModuleFiles &&
        !selectedTenderFolders.length
      ) {
        return onResetItemsList();
      }

      const file = selectedFiles[0];

      appLog(
        `handleTenderFoldersFilesDownload`,
        selectedFiles,
        selectedModuleFiles,
        selectedFolders,
        selectedTenderFolders
      );

      // single file download
      if (
        selectedFiles.length === 1 &&
        !selectedFolders.length &&
        !selectedModuleFiles.length &&
        !selectedTenderFolders.length
      ) {
        appLog('single file cherry picker tender worked!', file);
        await downloadFileLastVersion(
          selectedFiles[0],
          fileVersionsContext
        ).catch((err) => {
          appLog('downloadFile -> err', err);
        });
        return onResetItemsList();
      }

      if (
        selectedModuleFiles.length === 1 &&
        !selectedFiles.length &&
        !selectedFolders.length &&
        !selectedTenderFolders.length
      ) {
        appLog(`downloadModuleFile cherry picker tender worked!`);
        await downloadModuleFile(selectedModuleFiles[0], () => {
          // message.info(intl.formatMessage({ id: 'download-starting' }))
        }).catch((err) => {
          appLog('downloadFile -> err', err);
          message.error(intl.formatMessage({ id: 'file-download-failed' }));
        });

        return onResetItemsList();
      }

      // single folder download
      if (
        selectedTenderFolders.length === 1 &&
        !selectedFolders.length &&
        !selectedFiles.length &&
        !selectedModuleFiles.length
      ) {
        appLog(
          `downloadFolder cherry picker tender worked!`,
          selectedTenderFolders[0],
          allFolders
        );
        await downloadTenderFolder({
          // @ts-ignore
          folder: selectedTenderFolders[0],
          allFolders: allFolders || [],
          fileVersionsContext,
          onDownloadStarted: () => {
            // hidePrepMsg();
            // setTimeout(
            //   () =>
            //     message.info(intl.formatMessage({ id: 'download-starting' })),
            //   400
            // );
          },
          onLargeFiles: (largeFiles) => setLargeFilesContext(largeFiles),
        }).catch((err) => {
          appLog('downloadFolder -> err', err);
          // hidePrepMsg && hidePrepMsg();
          message.error(intl.formatMessage({ id: 'file-download-failed' }));
        });

        // hide cherry pick panel
        return onResetItemsList();
      }

      // multiple items download
      appLog(
        `Download Multiple Version files`,
        selectedFiles,
        selectedModuleFiles,
        selectedFolders,
        selectedTenderFolders
      );
      // For root documents view, use the existing function
      await downloadTenderMultiple({
        files: selectedFiles,
        folders: selectedTenderFolders as any,
        moduleFiles: selectedModuleFiles,
        allFolders: allFolders || [],
        fileVersionsContext,
        onDownloadStarted: () => {
          // hidePrepMsg();
          // setTimeout(
          //   () => message.info(intl.formatMessage({ id: 'download-starting' })),
          //   400
          // );
        },
        onLargeFiles: (largeFiles) => setLargeFilesContext(largeFiles),
      }).catch((err) => {
        appLog('downloadMultiple -> err', err);
        // hidePrepMsg && hidePrepMsg();
        message.error(intl.formatMessage({ id: 'file-download-failed' }));
      });
      return onResetItemsList();
    },
    []
  );

  const handleClassicRegistrationsDownload = useCallback(
    async (
      { classicRegistrations }: InputType,
      { onResetItemsList }: DownloadHandlerOptions
    ) => {
      if (!classicRegistrations || classicRegistrations.length === 0) {
        return onResetItemsList();
      }

      try {
        message.info(intl.formatMessage({ id: 'download-starting' }));

        await downloadMultipleClassicRegistrations({
          classicRegistrations,
          onDownloadStarted: () => {
            // Message already shown above
          },
        });

        message.success(intl.formatMessage({ id: 'download-success' }));
      } catch (err) {
        appLog('handleClassicRegistrationsDownload error', err);
        message.error(intl.formatMessage({ id: 'file-download-failed' }));
      }

      return onResetItemsList();
    },
    [intl]
  );

  const handleDownloadBasedOnPage = useCallback(
    async (inputData: InputType, opts: DownloadHandlerOptions) => {
      // Check if we have classic registrations (must check before isOnModule!)
      if (
        inputData.classicRegistrations &&
        inputData.classicRegistrations.length > 0
      ) {
        return handleClassicRegistrationsDownload(inputData, opts);
      }

      // 1. Check for Shared/MyTenders FIRST to ensure we use the public downloader
      if (isOnMyTenders) return handleMyTendersDownload(inputData, opts);

      // 2. Then proceed with internal authenticated checks
      if (isOnArchive) return handleArchiveFilesDownload(inputData, opts);
      if (isOnRecents) return handleArchiveFilesDownload(inputData, opts);
      if (isOnFavourites) return handleArchiveFilesDownload(inputData, opts);
      if (isOnSearch) return handleArchiveFilesDownload(inputData, opts);
      if (isOnModule) return handleModuleFilesDownload(inputData, opts);
      if (isOnTender) return handleTenderFilesDownload(inputData, opts);
      if (isOnTenderFolder)
        return handleTenderFoldersFilesDownload(inputData, opts);

      //fallback
      appLog('useCherryPickFileDownload -> no handler matched', {
        pathname: location.pathname,
        inputData,
      });
      return opts.onResetItemsList();
    },
    []
  );

  return {
    handleDownloadBasedOnPage,
  };
};
